import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Send, User, BellPlus, MailCheck, FolderOpen, FileText, Copy, ShieldQuestion, Plus, History, Trash2, X, Mail, ExternalLink, Paperclip, MailQuestion, Ticket, Repeat } from "lucide-react";
import { api } from "@/lib/api";
import { cadaCuantoTitulo } from "@/lib/recordatorios";
import { pideDia } from "@/components/recordatorios/PasoCuando";
import { Asistente } from "@/components/recordatorios/Asistente";
import { abrirVisor } from "@/lib/visor";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CATEGORIAS } from "@/pages/Contactos";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const QUICK = [
  "Quiero guardar un recordatorio",
  "¿Qué tengo pendiente esta semana?",
  "¿Qué vence este mes?",
  "¿Qué necesita mi atención?",
];

const ACTION_META = {
  crear_recordatorio: { icon: BellPlus, label: "Crear recordatorio", cta: "Crear recordatorio" },
  crear_recordatorio_puntual: { icon: BellPlus, label: "Crear recordatorio", cta: "Crear recordatorio" },
  crear_recordatorio_periodico: { icon: Repeat, label: "Recordatorio que se repite", cta: "Crear recordatorio" },
  activar_avisos: { icon: MailCheck, label: "Activar avisos por email", cta: "Activar avisos" },
  preparar_correo: { icon: MailCheck, label: "Preparar un correo", cta: "Preparar correo" },
  abrir_asunto: { icon: FolderOpen, label: "Abrir asunto", cta: "Abrir asunto" },
  mostrar_documentos: { icon: FileText, label: "Ver documentos", cta: "Ver documentos" },
  abrir_plan: { icon: Ticket, label: "Abrir plan", cta: "Abrir plan" },
  ver_entrada: { icon: FileText, label: "Ver entrada o reserva", cta: "Ver entrada o reserva" },
};

const newId = () => `conv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const greeting = (name) => ({
  role: "bot",
  text: `Hola, ${name || ""}. Soy tu agente AL DÍA. Pregúntame por tus asuntos, vencimientos, planes o documentos. También puedo apuntarte un recordatorio: dime qué y cuándo, por ejemplo «Recuérdame el viernes llamar al dentista».`,
});


export default function Agente() {
  const navigate = useNavigate();
  const { agenteChat, crearRecordatorio, guardarRecordatorioPuntual, actualizarAvisos, agenteConversaciones, agenteMensajes, agenteBorrarConversacion, documents, enviarCorreoAMi, registrarCorreo } = useData();
  const { user } = useAuth();
  const [activeId, setActiveId] = useState(newId());
  const [messages, setMessages] = useState([greeting(user?.nombre)]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [doneAction, setDoneAction] = useState({});
  const [convos, setConvos] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [correoDraft, setCorreoDraft] = useState(null);
  const [asistente, setAsistente] = useState(null);
  const endRef = useRef(null);

  const refreshConvos = async () => {
    try { setConvos(await agenteConversaciones()); } catch { /* noop */ }
  };
  useEffect(() => { refreshConvos(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (messages.length > 1 || typing) endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [messages, typing]);

  const nuevaConversacion = () => {
    setActiveId(newId());
    setMessages([greeting(user?.nombre)]);
    setSuggestions([]);
    setInput("");
    setShowHistory(false);
  };

  const cargarConversacion = async (cid) => {
    try {
      const data = await agenteMensajes(cid);
      const msgs = (data.mensajes || []).map((m) => ({
        role: m.role === "user" ? "user" : "bot",
        text: m.content,
      }));
      setActiveId(cid);
      setMessages([greeting(user?.nombre), ...msgs]);
      setSuggestions([]);
      setShowHistory(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo abrir la conversación.");
    }
  };

  const borrarConversacion = async (cid, e) => {
    e?.stopPropagation();
    try {
      await agenteBorrarConversacion(cid);
      await refreshConvos();
      if (cid === activeId) nuevaConversacion();
      toast.success("Conversación eliminada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar.");
    }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || typing) return;
    setMessages((prev) => [...prev, { role: "user", text: q }]);
    setInput("");
    setSuggestions([]);
    setTyping(true);
    try {
      const data = await agenteChat(activeId, q);
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: data.respuesta, verify: data.requiere_verificacion, action: data.accion_propuesta || null, actionKey: `${prev.length}-${Date.now()}` },
      ]);
      setSuggestions(data.sugerencias || []);
      refreshConvos();
    } catch (err) {
      setMessages((prev) => [...prev, { role: "bot", text: formatApiErrorDetail(err.response?.data?.detail) || "No he podido responder ahora mismo. Inténtalo de nuevo." }]);
    } finally {
      setTyping(false);
    }
  };

  const runAction = async (msg) => {
    const a = msg.action;
    if (!a) return;
    try {
      if (a.tipo === "crear_recordatorio") {
        if (!a.asunto_id || !a.reminder_date) return toast.error("Falta información para el recordatorio.");
        await crearRecordatorio({ asunto_id: a.asunto_id, reminder_date: a.reminder_date, text: a.reminder_text || "" });
        toast.success("Recordatorio creado");
        setDoneAction((d) => ({ ...d, [msg.actionKey]: "Recordatorio creado ✓" }));
      } else if (a.tipo === "crear_recordatorio_puntual") {
        if (!a.que || !a.fecha) return toast.error("Falta información para el recordatorio.");
        await guardarRecordatorioPuntual(null, { que: a.que, fecha: a.fecha, hora: a.hora || "", avisos: ["hora"] });
        toast.success("Recordatorio creado", { description: "Ya aparece en tu Agenda." });
        setDoneAction((d) => ({ ...d, [msg.actionKey]: "Recordatorio creado ✓" }));
      } else if (a.tipo === "crear_recordatorio_periodico") {
        await api.post("/gestiones", { ...periodicoBase(a), modo: "aviso", con_aviso: true, para: "yo" });
        window.dispatchEvent(new Event("aldia-gestiones"));
        toast.success("Recordatorio guardado", { description: "Se repetirá y aparecerá en tu Agenda." });
        setDoneAction((d) => ({ ...d, [msg.actionKey]: "Recordatorio guardado ✓" }));
      } else if (a.tipo === "activar_avisos") {
        if (!a.asunto_id) return toast.error("Falta el asunto.");
        await actualizarAvisos(a.asunto_id, { aviso_offsets: a.aviso_offsets || [7], avisos_activos: true });
        toast.success("Avisos activados");
        setDoneAction((d) => ({ ...d, [msg.actionKey]: "Avisos activados ✓" }));
      } else if (a.tipo === "preparar_correo") {
        setCorreoDraft({
          actionKey: msg.actionKey,
          asunto_id: a.asunto_id || null,
          destinatario: a.email_borrador?.destinatario || "",
          asunto: a.email_borrador?.asunto || "",
          cuerpo: a.email_borrador?.cuerpo || "",
          docIds: [],
        });
      } else if (a.tipo === "abrir_asunto") {
        navigate(`/asunto/${a.asunto_id}`);
      } else if (a.tipo === "mostrar_documentos") {
        navigate("/documentos");
      } else if (a.tipo === "abrir_plan") {
        navigate(`/ocio?plan=${a.plan_id}`);
      } else if (a.tipo === "ver_entrada") {
        abrirVisor({ id: a.documento_id, title: a.titulo });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo completar la acción.");
    }
  };

  const showQuick = messages.length <= 1;
  const chips = showQuick ? QUICK : suggestions;

  return (
    <div className="flex flex-col h-[calc(100vh-10rem)] min-h-[260px]" style={{ height: "calc(var(--app-h, 100dvh) - 10rem)" }}>
      <header className="aldia-fade-up flex items-center gap-3 pb-4 border-b border-[#E5DFD5]">
        <div className="w-11 h-11 rounded-full bg-[#3A6B56] flex items-center justify-center shrink-0">
          <Bot size={24} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-lg font-bold text-[#1C2421]">Tu agente AL DÍA</h1>
          <p className="text-sm text-[#6E7A74]">Responde con tus datos guardados</p>
        </div>
        <button data-testid="agente-new-chat" onClick={nuevaConversacion} aria-label="Nueva conversación"
          className="w-11 h-11 rounded-full bg-[#F2EDE4] flex items-center justify-center hover:bg-[#E5DFD5] shrink-0">
          <Plus size={22} className="text-[#3A6B56]" />
        </button>
        <button data-testid="agente-history-toggle" onClick={() => { setShowHistory((s) => !s); refreshConvos(); }} aria-label="Conversaciones recientes"
          className="w-11 h-11 rounded-full bg-[#F2EDE4] flex items-center justify-center hover:bg-[#E5DFD5] shrink-0">
          <History size={22} className="text-[#3A6B56]" />
        </button>
      </header>

      {showHistory && (
        <div className="border-b border-[#E5DFD5] py-3" data-testid="agente-history-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-[#6E7A74]">Conversaciones recientes</p>
            <button onClick={() => setShowHistory(false)} aria-label="Cerrar historial"><X size={18} className="text-[#9AA39D]" /></button>
          </div>
          {convos.length === 0 ? (
            <p className="text-sm text-[#9AA39D] py-2">No hay conversaciones guardadas todavía.</p>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {convos.map((c) => (
                <div key={c.id} data-testid={`agente-conv-${c.id}`}
                  onClick={() => cargarConversacion(c.id)}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2.5 cursor-pointer border ${c.id === activeId ? "bg-[#E8F0EC] border-[#3A6B56]" : "bg-white border-[#E5DFD5] hover:border-[#3A6B56]"}`}>
                  <History size={16} className="text-[#3A6B56] shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-base text-[#1C2421]">{c.title}</span>
                  <button data-testid={`agente-conv-delete-${c.id}`} onClick={(e) => borrarConversacion(c.id, e)}
                    aria-label="Eliminar conversación" className="w-8 h-8 rounded-lg bg-[#FEF2F2] flex items-center justify-center hover:bg-[#FEE2E2] shrink-0">
                    <Trash2 size={15} className="text-[#B91C1C]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto py-4 space-y-4" data-testid="agente-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "bot" && (
              <div className="shrink-0 w-9 h-9 rounded-full bg-[#E8F0EC] flex items-center justify-center self-end">
                <Bot size={18} className="text-[#3A6B56]" />
              </div>
            )}
            <div className="max-w-[82%] space-y-2">
              <div className={`rounded-2xl px-4 py-3 text-base leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-[#3A6B56] text-white rounded-br-md" : "bg-white border border-[#E5DFD5] text-[#1C2421] rounded-bl-md"}`}>
                {m.verify && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#B45309] bg-[#FEF3C7] rounded-full px-2 py-0.5 mb-1.5">
                    <ShieldQuestion size={12} /> Conviene que lo verifiques
                  </span>
                )}
                <div>{m.text}</div>
              </div>
              {m.action && ACTION_META[m.action.tipo] && (
                <ActionCard action={m.action} done={doneAction[m.actionKey]} onRun={() => runAction(m)}
                  onCorreo={() => setAsistente({ key: m.actionKey, g: { ...periodicoBase(m.action), modo: "correo" } })} />
              )}
            </div>
            {m.role === "user" && (
              <div className="shrink-0 w-9 h-9 rounded-full bg-[#F2EDE4] flex items-center justify-center self-end">
                <User size={18} className="text-[#4A5550]" />
              </div>
            )}
          </div>
        ))}
        {typing && (
          <div className="flex gap-2.5">
            <div className="shrink-0 w-9 h-9 rounded-full bg-[#E8F0EC] flex items-center justify-center"><Bot size={18} className="text-[#3A6B56]" /></div>
            <div className="bg-white border border-[#E5DFD5] rounded-2xl px-4 py-3 flex gap-1.5"><Dot /> <Dot d={150} /> <Dot d={300} /></div>
          </div>
        )}
        <div ref={endRef} />
        {chips.length > 0 && (
          <div className="pt-2" data-testid="agente-quick">
            {showQuick && <p className="text-sm font-semibold text-[#6E7A74] mb-2">Preguntas rápidas</p>}
            <div className="flex flex-wrap gap-2">
              {chips.map((q, i) => (
                <button key={`${q}-${i}`} data-testid={`agente-quick-${i}`} onClick={() => send(q)}
                  className="text-left bg-white border border-[#E5DFD5] rounded-full px-3.5 py-2.5 text-sm font-medium text-[#3A6B56] hover:border-[#3A6B56] hover:bg-[#F6F3EE] transition-colors">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

      </div>

      <div className="flex items-center gap-2 pt-1">
        <input
          data-testid="agente-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Escribe tu pregunta…"
          className="flex-1 min-h-[56px] px-5 rounded-full bg-white border border-[#E5DFD5] text-base text-[#1C2421] placeholder-[#9AA39D] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20 focus:border-[#3A6B56]"
        />
        <button data-testid="agente-send" onClick={() => send()} aria-label="Enviar"
          className="shrink-0 w-14 h-14 rounded-full bg-[#3A6B56] text-white flex items-center justify-center hover:bg-[#2E5645] transition-colors">
          <Send size={24} />
        </button>
      </div>
      <p data-testid="agente-teclado-hint" className="pt-2 px-2 text-sm text-[#6E7A74]">Para hablar en vez de escribir, pulsa el micrófono de tu teclado.</p>

      <CorreoDraftModal
        draft={correoDraft}
        onClose={() => setCorreoDraft(null)}
        documents={documents || []}
        userEmail={user?.email}
        enviarCorreoAMi={enviarCorreoAMi}
        registrarCorreo={registrarCorreo}
        onDone={(key, label) => { setDoneAction((d) => ({ ...d, [key]: label })); setCorreoDraft(null); }}
      />
      {asistente && <Asistente g={asistente.g} onCerrar={() => setAsistente(null)}
        onVer={(id) => { setDoneAction((d) => ({ ...d, [asistente.key]: "Recordatorio guardado ✓" })); navigate(`/gestiones/${id}`); }} />}
    </div>
  );
}

const SEG_OPCIONES = [
  { dias: 3, label: "3 días" },
  { dias: 7, label: "7 días" },
  { dias: 15, label: "15 días" },
];

const CorreoDraftModal = ({ draft, onClose, documents, userEmail, enviarCorreoAMi, registrarCorreo, onDone }) => {
  const { contactos, crearSeguimiento, crearContacto } = useData();
  const [dest, setDest] = useState("");
  const [asunto, setAsunto] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [docIds, setDocIds] = useState([]);
  const [step, setStep] = useState("form"); // "form" | "confirm" | "seguimiento"
  const [confirmMode, setConfirmMode] = useState(null); // "enviar" | "abrir"
  const [sending, setSending] = useState(false);
  const [segDias, setSegDias] = useState(7);
  const [segFecha, setSegFecha] = useState("");
  const [savingSeg, setSavingSeg] = useState(false);
  const [showNuevoContacto, setShowNuevoContacto] = useState(false);
  const [ncNombre, setNcNombre] = useState("");
  const [ncCategoria, setNcCategoria] = useState("otros");
  const [ncNotas, setNcNotas] = useState("");
  const [savingContacto, setSavingContacto] = useState(false);
  const doneRef = useRef({ correoId: null, label: "" });

  useEffect(() => {
    if (draft) {
      setDest(draft.destinatario || "");
      setAsunto(draft.asunto || "");
      setCuerpo(draft.cuerpo || "");
      setDocIds(draft.docIds || []);
      setStep("form");
      setConfirmMode(null);
      setSending(false);
      setSegDias(7);
      setSegFecha("");
      setSavingSeg(false);
      setShowNuevoContacto(false);
      setNcNombre("");
      setNcCategoria("otros");
      setNcNotas("");
    }
  }, [draft]);

  if (!draft) return null;
  const destValido = EMAIL_RE.test(dest.trim());
  const destEnAgenda = (contactos || []).some((c) => (c.email || "").toLowerCase() === dest.trim().toLowerCase());
  const docsDisponibles = (documents || []).filter((d) => d.confirmado && !d.is_deleted);
  const toggleDoc = (id) =>
    setDocIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const guardarContactoAlVuelo = async () => {
    if (!ncNombre.trim()) { toast.error("Escribe un nombre para el contacto."); return; }
    setSavingContacto(true);
    try {
      await crearContacto({ nombre: ncNombre.trim(), email: dest.trim(), categoria: ncCategoria, notas: ncNotas.trim() });
      toast.success("Contacto guardado en tu agenda");
      setShowNuevoContacto(false);
      setNcNombre(""); setNcCategoria("otros"); setNcNotas("");
    } catch {
      toast.error("No se pudo guardar el contacto.");
    } finally {
      setSavingContacto(false);
    }
  };

  const validar = () => {
    if (!destValido) { toast.error("Escribe un destinatario válido (un solo correo)."); return false; }
    if (!asunto.trim()) { toast.error("El asunto no puede estar vacío."); return false; }
    if (!cuerpo.trim()) { toast.error("El cuerpo no puede estar vacío."); return false; }
    return true;
  };
  const pedir = (modo) => { if (validar()) { setConfirmMode(modo); setStep("confirm"); } };

  const irASeguimiento = (correoId, label) => {
    doneRef.current = { correoId, label };
    setStep("seguimiento");
  };

  const confirmarEnviar = async () => {
    setSending(true);
    try {
      const r = await enviarCorreoAMi({
        asunto_id: draft.asunto_id, destinatario_final: dest.trim(),
        asunto: asunto.trim(), cuerpo: cuerpo.trim(), documentos_ids: docIds,
      });
      toast.success(`Enviado a tu correo (${userEmail || "tu dirección"})`, {
        description: `Reenvíalo a ${dest.trim()} desde tu bandeja.`,
      });
      irASeguimiento(r?.registro?.id || null, "Enviado a tu correo ✓");
    } catch (err) {
      setStep("form"); setConfirmMode(null); // permite reintentar; NO se marca como enviado
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar. Puedes reintentarlo.");
    } finally {
      setSending(false);
    }
  };

  const confirmarAbrir = async () => {
    const mailto = `mailto:${encodeURIComponent(dest.trim())}?subject=${encodeURIComponent(asunto.trim())}&body=${encodeURIComponent(cuerpo.trim())}`;
    window.location.href = mailto;
    let correoId = null;
    try {
      const r = await registrarCorreo({ asunto_id: draft.asunto_id, destinatario_final: dest.trim(), estado: "abierto_en_mi_correo" });
      correoId = r?.registro?.id || null;
    } catch { /* registro best-effort */ }
    toast.success("Abrimos tu app de correo con el borrador listo.");
    irASeguimiento(correoId, "Abierto en tu correo ✓");
  };

  const descartar = async () => {
    try {
      await registrarCorreo({ asunto_id: draft.asunto_id, destinatario_final: dest.trim() || null, estado: "cancelado" });
    } catch { /* noop */ }
    toast.info("Borrador descartado.");
    onDone(draft.actionKey, "Borrador descartado");
  };

  const crearRecordatorioSeg = async () => {
    setSavingSeg(true);
    try {
      await crearSeguimiento({
        correo_id: doneRef.current.correoId, asunto_id: draft.asunto_id || null,
        destinatario: dest.trim() || null,
        ...(segFecha ? { fecha: segFecha } : { dias: segDias }),
      });
      toast.success("Recordatorio de seguimiento creado", { description: "Lo verás en Inicio y Próximos." });
      onDone(draft.actionKey, doneRef.current.label + " · seguimiento ✓");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo crear el seguimiento.");
      setSavingSeg(false);
    }
  };
  const omitirSeg = () => onDone(draft.actionKey, doneRef.current.label);

  return (
    <Dialog open={!!draft} onOpenChange={(o) => { if (!o && !sending && !savingSeg) onClose(); }}>
      <DialogContent className="bg-[#FAF8F5] border-[#E5DFD5] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="correo-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#1C2421] flex items-center gap-2">
            <Mail size={20} className="text-[#3A6B56]" /> Preparar correo
          </DialogTitle>
          <DialogDescription className="text-[#6E7A74]">
            Revisa y edita el borrador. Nada se envía sin tu confirmación.
          </DialogDescription>
        </DialogHeader>

        {step === "confirm" ? (
          <div className="py-4" data-testid="correo-confirm-panel">
            <div className="flex items-start gap-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-xl p-4 mb-4">
              <ShieldQuestion size={22} className="text-[#B45309] shrink-0 mt-0.5" />
              <div data-testid="correo-confirm-question">
                <p className="text-sm font-semibold text-[#B45309] mb-1">
                  {confirmMode === "enviar" ? "Acción: Enviarme una copia" : "Acción: Abrir en mi correo"}
                </p>
                <p className="text-base text-[#1C2421]">
                  {confirmMode === "enviar"
                    ? `¿Confirmas que quieres recibir una copia de este borrador en tu correo (${userEmail || "tu dirección"})? No se enviará al destinatario final.`
                    : `¿Confirmas que quieres abrir tu app de correo para enviarlo a ${dest.trim()} desde tu propia cuenta?`}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="correo-confirm-volver"
                onClick={() => { setStep("form"); setConfirmMode(null); }}
                disabled={sending}
                className="flex-1 min-h-[52px] rounded-xl bg-white border border-[#E5DFD5] text-[#4A5550] font-semibold hover:bg-[#F6F3EE] transition-colors"
              >
                Volver
              </button>
              <button
                data-testid="correo-confirm-si"
                onClick={confirmMode === "enviar" ? confirmarEnviar : confirmarAbrir}
                disabled={sending}
                className="flex-1 min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold hover:bg-[#2E5645] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {sending ? "Procesando…" : "Sí, confirmar"}
              </button>
            </div>
          </div>
        ) : step === "seguimiento" ? (
          <div className="py-3" data-testid="correo-seguimiento-panel">
            <div className="flex items-start gap-3 bg-[#E8F0EC] border border-[#C9DED3] rounded-xl p-4 mb-4">
              <MailQuestion size={22} className="text-[#3A6B56] shrink-0 mt-0.5" />
              <p className="text-base text-[#1C2421]">
                ¿Quieres que te recuerde hacer seguimiento si no recibes respuesta? El recordatorio será:
                «Comprobar si has recibido respuesta.»
              </p>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {SEG_OPCIONES.map((o) => (
                <button
                  key={o.dias}
                  data-testid={`seguimiento-dias-${o.dias}`}
                  onClick={() => { setSegDias(o.dias); setSegFecha(""); }}
                  className={`min-h-[44px] px-4 rounded-full text-sm font-semibold border transition-colors ${
                    !segFecha && segDias === o.dias ? "bg-[#3A6B56] text-white border-[#3A6B56]" : "bg-white text-[#4A5550] border-[#E5DFD5] hover:border-[#3A6B56]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">O elige una fecha</label>
              <input
                data-testid="seguimiento-fecha"
                type="date"
                value={segFecha}
                onChange={(e) => setSegFecha(e.target.value)}
                className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]"
              />
            </div>
            <div className="flex gap-3">
              <button
                data-testid="seguimiento-omitir"
                onClick={omitirSeg}
                disabled={savingSeg}
                className="flex-1 min-h-[52px] rounded-xl bg-white border border-[#E5DFD5] text-[#4A5550] font-semibold hover:bg-[#F6F3EE] transition-colors"
              >
                Ahora no
              </button>
              <button
                data-testid="seguimiento-crear"
                onClick={crearRecordatorioSeg}
                disabled={savingSeg}
                className="flex-1 min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold hover:bg-[#2E5645] transition-colors disabled:opacity-60"
              >
                {savingSeg ? "Creando…" : "Crear recordatorio"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-1">
            {(contactos || []).some((c) => c.email) && (
              <div>
                <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Elegir de la agenda (opcional)</label>
                <select
                  data-testid="correo-contacto-select"
                  defaultValue=""
                  onChange={(e) => { const c = (contactos || []).find((x) => x.id === e.target.value); if (c) setDest(c.email); }}
                  className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]"
                >
                  <option value="">— Elegir un contacto guardado —</option>
                  {contactos.filter((c) => c.email).map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre} · {c.email}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Destinatario final (el tercero)</label>
              <input
                data-testid="correo-dest-input"
                type="email"
                value={dest}
                onChange={(e) => setDest(e.target.value)}
                placeholder="correo@ejemplo.com"
                className={`w-full min-h-[52px] px-4 rounded-xl bg-white border text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56] ${dest && !destValido ? "border-[#FCA5A5]" : "border-[#E5DFD5]"}`}
              />
              {dest && !destValido && <p className="text-xs text-[#B91C1C] mt-1">Escribe un único correo válido.</p>}
              {destValido && !destEnAgenda && !showNuevoContacto && (
                <button
                  data-testid="correo-guardar-contacto-btn"
                  onClick={() => { setShowNuevoContacto(true); setNcNombre(draft.destinatario_nombre || ""); }}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3A6B56] hover:underline"
                >
                  <Plus size={15} /> Guardar este contacto
                </button>
              )}
              {destValido && destEnAgenda && (
                <p className="text-xs text-[#3A6B56] mt-1.5 flex items-center gap-1"><Mail size={13} /> Ya está en tu agenda.</p>
              )}
              {showNuevoContacto && (
                <div className="mt-3 bg-white border border-[#E5DFD5] rounded-xl p-3.5 space-y-3" data-testid="correo-nuevo-contacto-form">
                  <p className="text-sm font-semibold text-[#1C2421]">Guardar «{dest.trim()}» en tu agenda</p>
                  <input
                    data-testid="nc-nombre-input"
                    value={ncNombre}
                    onChange={(e) => setNcNombre(e.target.value)}
                    placeholder="Nombre (p. ej. Gestoría López)"
                    className="w-full min-h-[48px] px-4 rounded-xl bg-[#FAF8F5] border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]"
                  />
                  <select
                    data-testid="nc-categoria-select"
                    value={ncCategoria}
                    onChange={(e) => setNcCategoria(e.target.value)}
                    className="w-full min-h-[48px] px-4 rounded-xl bg-[#FAF8F5] border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]"
                  >
                    {CATEGORIAS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                  <input
                    data-testid="nc-notas-input"
                    value={ncNotas}
                    onChange={(e) => setNcNotas(e.target.value)}
                    placeholder="Notas (opcional)"
                    className="w-full min-h-[48px] px-4 rounded-xl bg-[#FAF8F5] border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]"
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid="nc-cancelar-btn"
                      onClick={() => setShowNuevoContacto(false)}
                      disabled={savingContacto}
                      className="flex-1 min-h-[46px] rounded-xl bg-white border border-[#E5DFD5] text-[#4A5550] font-semibold text-sm hover:bg-[#F6F3EE]"
                    >
                      Cancelar
                    </button>
                    <button
                      data-testid="nc-guardar-btn"
                      onClick={guardarContactoAlVuelo}
                      disabled={savingContacto}
                      className="flex-1 min-h-[46px] rounded-xl bg-[#3A6B56] text-white font-semibold text-sm hover:bg-[#2E5645] disabled:opacity-60"
                    >
                      {savingContacto ? "Guardando…" : "Guardar contacto"}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Asunto</label>
              <input
                data-testid="correo-asunto-input"
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Cuerpo</label>
              <textarea
                data-testid="correo-cuerpo-input"
                value={cuerpo}
                onChange={(e) => setCuerpo(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] leading-relaxed focus:outline-none focus:border-[#3A6B56] resize-y"
              />
            </div>
            {docsDisponibles.length > 0 && (
              <div>
                <label className="block text-sm font-semibold text-[#4A5550] mb-1.5 flex items-center gap-1.5">
                  <Paperclip size={15} /> Adjuntar documentos (opcional)
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {docsDisponibles.map((d) => (
                    <button
                      key={d.id}
                      data-testid={`correo-doc-${d.id}`}
                      onClick={() => toggleDoc(d.id)}
                      className="w-full flex items-center justify-between gap-2 px-3.5 min-h-[48px] rounded-xl bg-white border border-[#E5DFD5] hover:border-[#3A6B56] transition-colors text-left"
                    >
                      <span className="flex items-center gap-2 text-sm text-[#1C2421] truncate">
                        <FileText size={16} className="text-[#3A6B56] shrink-0" />
                        <span className="truncate">{d.original_filename || d.filename || "Documento"}</span>
                      </span>
                      <span className={`w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center ${docIds.includes(d.id) ? "bg-[#3A6B56] border-[#3A6B56]" : "border-[#D6CFC2]"}`}>
                        {docIds.includes(d.id) && <span className="text-white text-xs leading-none">✓</span>}
                      </span>
                    </button>
                  ))}
                </div>
                {docIds.length > 0 && (
                  <p className="text-xs text-[#6E7A74] mt-1.5">
                    Los adjuntos se incluyen al <strong>enviarte una copia</strong>. En «Abrir en mi correo» no se adjuntan (el correo tiene esa limitación).
                  </p>
                )}
              </div>
            )}

            <div className="pt-1 space-y-2.5">
              <div>
                <button
                  data-testid="correo-enviar-a-mi-btn"
                  onClick={() => pedir("enviar")}
                  className="w-full min-h-[54px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] transition-colors flex items-center justify-center gap-2"
                >
                  <Mail size={20} /> Enviarme una copia
                </button>
                <p className="text-xs text-[#6E7A74] mt-1.5 px-1" data-testid="correo-enviar-a-mi-hint">
                  Recibirás este borrador en tu correo. No se enviará al destinatario final.
                </p>
              </div>
              <button
                data-testid="correo-abrir-mi-btn"
                onClick={() => pedir("abrir")}
                className="w-full min-h-[54px] rounded-xl bg-white border-2 border-[#3A6B56] text-[#3A6B56] font-semibold text-base hover:bg-[#F2F6F4] transition-colors flex items-center justify-center gap-2"
              >
                <ExternalLink size={20} /> Abrir en mi correo
              </button>
              <button
                data-testid="correo-descartar-btn"
                onClick={descartar}
                className="w-full min-h-[48px] rounded-xl text-[#B91C1C] font-medium text-sm hover:bg-[#FEF2F2] transition-colors"
              >
                Descartar borrador
              </button>
            </div>
            <p className="text-xs text-[#6E7A74] leading-relaxed">
              Por seguridad, AL DÍA solo envía a tu propia dirección. Para que llegue al tercero,
              reenvíalo tú desde tu correo o usa «Abrir en mi correo».
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const periodicoBase = (a) => ({
  titulo: a.que, frecuencia: a.frecuencia, fecha_inicio: a.fecha, cada_n: a.cada_n ?? null, unidad: a.unidad ?? null,
  dia: pideDia({ frecuencia: a.frecuencia, unidad: a.unidad }) ? Number(a.fecha.split("-")[2]) : null,
});

const ActionCard = ({ action, done, onRun, onCorreo }) => {
  const meta = ACTION_META[action.tipo];
  const Icon = meta.icon;
  return (
    <div className="bg-[#F6F3EE] border border-[#E5DFD5] rounded-2xl p-3.5" data-testid="agente-action-card">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} className="text-[#3A6B56]" />
        <p className="font-heading font-bold text-sm text-[#1C2421]">{meta.label}</p>
      </div>
      <div className="text-sm text-[#4A5550] space-y-0.5 mb-3">
        {action.titulo && <p><span className="text-[#6E7A74]">{["abrir_plan", "ver_entrada"].includes(action.tipo) ? "Plan:" : "Asunto:"}</span> {action.titulo}</p>}
        {action.tipo === "crear_recordatorio" && (
          <p><span className="text-[#6E7A74]">Avísame el:</span> {action.reminder_date} — {action.reminder_text}</p>
        )}
        {action.tipo === "crear_recordatorio_puntual" && (
          <p data-testid="agente-action-puntual"><span className="text-[#6E7A74]">Recordatorio:</span> {action.que} — {new Date(`${action.fecha}T12:00:00`).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}{action.hora ? ` a las ${action.hora}` : ""}</p>
        )}
        {action.tipo === "crear_recordatorio_periodico" && (
          <p data-testid="agente-action-periodico"><span className="text-[#6E7A74]">Recordatorio:</span> {action.que} — {cadaCuantoTitulo(periodicoBase(action))}. Primera vez: {new Date(`${action.fecha}T12:00:00`).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}</p>
        )}
        {action.tipo === "activar_avisos" && (
          <p><span className="text-[#6E7A74]">Avisos:</span> {(action.aviso_offsets || [7]).join(", ")} días antes</p>
        )}
        {action.tipo === "preparar_correo" && action.email_borrador && (
          <div className="bg-white border border-[#E5DFD5] rounded-lg p-2.5 mt-1">
            {action.email_borrador.destinatario && (
              <p className="text-[#6E7A74]">Para: <span className="text-[#1C2421]">{action.email_borrador.destinatario}</span></p>
            )}
            <p className="font-semibold text-[#1C2421]">{action.email_borrador.asunto}</p>
            <p className="whitespace-pre-line text-[#4A5550] mt-1">{action.email_borrador.cuerpo}</p>
          </div>
        )}
      </div>
      {done ? (
        <p className="text-sm font-semibold text-[#15803D]" data-testid="agente-action-done">{done}</p>
      ) : (
        <button data-testid="agente-action-confirm" onClick={onRun}
          className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] transition-colors flex items-center justify-center gap-2">
          {action.tipo === "preparar_correo" && <Mail size={16} />}
          {meta.cta}
        </button>
      )}
      {!done && action.tipo === "crear_recordatorio_periodico" && (
        <button data-testid="agente-action-periodico-correo" onClick={onCorreo}
          className="mt-2 w-full min-h-[48px] rounded-xl bg-white border-2 border-[#3A6B56] text-[#3A6B56] font-semibold text-base hover:bg-[#F2F6F4] transition-colors flex items-center justify-center gap-2">
          <Mail size={16} /> Preparar también un correo
        </button>
      )}
    </div>
  );
};

const Dot = ({ d = 0 }) => (
  <span className="w-2 h-2 rounded-full bg-[#9AA39D] animate-bounce" style={{ animationDelay: `${d}ms` }} />
);
